import React, { useState } from 'react';
import { Table, Tag, Card, Descriptions, Spin, Pagination, Popover, Button, Modal, Typography, Form, message, Input, Flex } from 'antd';
import moment from 'moment';
import { useMediaQuery } from 'react-responsive';
import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import usePermissions from '../hooks/usePermissions';
import axiosInstance from '../services/axiosInstance';
import { catchError } from '../utils/errorHandler';

const { Title } = Typography

const FeedBackEvalErrors = ({ feedBackData, loading, pagination, setPagination, fetchFeedBackData }) => {
    const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
    const dataSource = feedBackData;
    const { hasPermission } = usePermissions();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentFeedBackUid, setCurrentFeedBackUid] = useState(null);
    const [formSubmitLoading, setFormSubmitLoading] = useState(false);
    const [oldRemarks, setOldRemarks] = useState("")

    const handleOpenModal = (feedBack) => {
        setCurrentFeedBackUid(feedBack?.uid);
        setOldRemarks(feedBack?.action || '')
        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setOldRemarks("")
    };

    const columns = [
        {
            title: 'Feedback ID',
            dataIndex: 'feedback',
            key: 'feedback',
        },
        {
            title: 'Evaluation Status',
            dataIndex: 'evaluation_status',
            key: 'evaluation_status',
            render: (status) => {
                const color = status === 'poor' ? 'red' : status === 'average' ? 'orange' : 'green';
                return <Tag color={color}>{status.toUpperCase()}</Tag>;
            },
        },
        {
            title: 'Completed By',
            dataIndex: 'complete_by',
            key: 'complete_by',
        },
        {
            title: 'Score',
            dataIndex: 'evaluation_score',
            key: 'evaluation_score',
        },
        {
            title: 'Completed At',
            dataIndex: 'mark_complete_at',
            key: 'mark_complete_at',
            render: (date) => moment(date).format('DD-MM-YYYY hh:mm A'),
        },
        {
            title: "Feedback Remarks",
            dataIndex: "feedback_remarks",
            key: "feedback_remarks",
            render: (remarks) => {
                const hasRemarks = Array.isArray(remarks) && remarks.length > 0;

                return hasRemarks ? (
                    <Popover
                        content={
                            <div style={{ maxWidth: "240px" }}>
                                <ul style={{ paddingLeft: 20, margin: 0 }}>
                                    {remarks.map((item, index) => (
                                        <li key={index} style={{ marginBottom: 4, wordBreak: "break-word" }}>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        }
                        title="Remarks"
                        trigger="hover"
                        placement="left"
                    >
                        <Button type="link" style={{ padding: 0 }}>
                            View
                        </Button>
                    </Popover>
                ) : (
                    <span style={{ color: "#999" }}>N/A</span>
                );
            },
        },
        {
            title: "Action Remarks",
            dataIndex: "action",
            key: "action",
            render: (text) => {
                const hasAction = !!text && text.trim() !== "";

                return hasAction ? (
                    <Popover
                        content={
                            <div style={{ wordBreak: "break-word", maxWidth: "200px" }}>
                                {text}
                            </div>
                        }
                        title="Action Remarks"
                        trigger="hover"
                        placement="left"
                    >
                        <Button type="link" style={{ padding: 0 }}>
                            View
                        </Button>
                    </Popover>
                ) : (
                    <span style={{ color: "#999" }}>N/A</span>
                );
            },
        }
        ,
        ...(hasPermission("can_action_flagged_submission_feedback")
            ? [
                {
                    title: "Action",
                    key: "action",
                    render: (_, record) => (
                        <Button onClick={() => handleOpenModal(record)} type="link">
                            <EditOutlined />
                        </Button>
                    ),
                },
            ]
            : []),

    ];

    if (loading) {
        return <Spin className="flex justify-center items-center w-full my-6" size="large" />;
    }

    return (
        <>
            {isMobile ? (
                <div style={{ padding: 0, margin: 0 }}>
                    {dataSource.map((item) => (
                        <Card
                            key={item.feedback}
                            bodyStyle={{ padding: 0, marginBottom: 16 }}
                            style={{ margin: 0, border: 'none', borderBottom: '1px solid #f0f0f0' }}
                            actions={[
                                hasPermission("can_action_flagged_submission_feedback") &&
                                (
                                    <Button
                                        type="link"
                                        icon={<EditOutlined />}
                                        onClick={() => handleOpenModal(item)}>
                                        Edit Remarks
                                    </Button>
                                ),
                            ]}
                        >
                            <Descriptions column={1} size="small" bordered style={{ margin: 0 }}>
                                <Descriptions.Item label="Feedback ID">{item.feedback}</Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <Tag
                                        color={
                                            item.evaluation_status === 'poor'
                                                ? 'red'
                                                : item.evaluation_status === 'average'
                                                    ? 'orange'
                                                    : 'green'
                                        }
                                    >
                                        {item.evaluation_status.toUpperCase()}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Completed By">{item.complete_by}</Descriptions.Item>
                                <Descriptions.Item label="Score">{item.evaluation_score}</Descriptions.Item>
                                <Descriptions.Item label="Completed At">
                                    {moment(item.mark_complete_at).format('DD-MM-YYYY hh:mm A')}
                                </Descriptions.Item>
                                <Descriptions.Item label="Feedback Remarks">
                                    {Array.isArray(item.feedback_remarks) && item.feedback_remarks.length > 0 ? (
                                        <Popover
                                            content={
                                                <div
                                                    style={{
                                                        maxWidth: "180px",
                                                        maxHeight: "120px",
                                                        overflowY: "auto",
                                                    }}
                                                >
                                                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                                                        {item.feedback_remarks.map((remark, index) => (
                                                            <li key={index} style={{ marginBottom: 4, wordBreak: "break-word" }}>
                                                                {remark}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            }
                                            title="Feedback Remarks"
                                            trigger="click"
                                        >
                                            <Button type="link" style={{ padding: 0 }}>
                                                View
                                            </Button>
                                        </Popover>
                                    ) : (
                                        <span style={{ color: "#999" }}>N/A</span>
                                    )}
                                </Descriptions.Item>

                                <Descriptions.Item label="Remarks">
                                    {item.action ? (
                                        <Popover
                                            content={
                                                <div
                                                    style={{
                                                        maxWidth: "140px",
                                                        maxHeight: "100px",
                                                        overflowY: "auto",
                                                        wordBreak: "break-word",
                                                    }}
                                                >
                                                    {item.action}
                                                </div>
                                            }
                                            title="Remarks"
                                            trigger="click"
                                        >
                                            <Button type="link" style={{ padding: 0 }}>
                                                View
                                            </Button>
                                        </Popover>
                                    ) : (
                                        <span style={{ color: "#999" }}>N/A</span>
                                    )}
                                </Descriptions.Item>

                            </Descriptions>
                        </Card>
                    ))}
                    <div style={{ textAlign: "center", marginTop: 16 }}>
                        <Pagination
                            current={pagination.current}
                            pageSize={pagination.pageSize}
                            total={pagination.total}
                            onChange={(page) => setPagination((prev) => ({ ...prev, current: page }))}
                            showSizeChanger={false}
                        />
                    </div>
                </div>
            ) : (
                <Table
                    dataSource={dataSource}
                    columns={columns}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        onChange: (page, pageSize) => {
                            setPagination({ ...pagination, current: page, pageSize });
                        },
                        onShowSizeChange: (current, size) => {
                            setPagination({ ...pagination, current: 1, pageSize: size });
                        },
                    }}
                    rowKey={(record) => record.feedback}
                    bordered
                />
            )}

            <Modal
                className="custom-modal2"
                title={
                    <div className="custom-modal-header" style={{ display: "flex", justifyContent: "space-between" }}>
                        <Title
                            level={3}
                            style={{
                                color: "white",
                                margin: 0,
                                position: "relative",
                                marginLeft: "10px",
                            }}
                        >
                            Edit Remarks
                        </Title>
                    </div>
                }
                open={isModalVisible}
                onCancel={handleCloseModal}
                footer={null}
                destroyOnClose
            >
                <Form
                    initialValues={{
                        action_remarks: oldRemarks
                    }}
                    onFinish={async (values) => {
                        setFormSubmitLoading(true);
                        try {
                            const response = await axiosInstance.post(
                                `/task/v2/flagged-submission/feedback/action/${currentFeedBackUid}/`,
                                { action_remarks: values.action_remarks }
                            );

                            if (response.status === 200) {
                                message.success(response.data.data);
                                fetchFeedBackData(pagination.current, pagination.pageSize);
                                handleCloseModal();
                                setFormSubmitLoading(false);
                            }
                        } catch (error) {
                            setFormSubmitLoading(false);
                            await catchError(error);
                        }
                    }}
                >
                    <Form.Item
                        name="action_remarks"
                        rules={[{ required: true, message: "Please write remarks" }]}
                    >
                        <Input.TextArea placeholder="Write remarks" />
                    </Form.Item>
                    <Flex align="flex-end" justify="flex-end" style={{ width: "100%" }}>
                        <Button type="primary" htmlType="submit">
                            {formSubmitLoading ? <Spin /> : "Submit"}
                        </Button>
                    </Flex>
                </Form>
            </Modal>
        </>
    );
};

export default FeedBackEvalErrors;
